/**
 * @file FontUsageList.tsx
 * @description Список «Все места · N» роли шрифта во вкладке «Шрифты»
 * (phase19.16; SB10, дополнение 2026-10-11; uiux-fonts-usage §3, §7):
 * группы «На страницах», «Товар и каталог», … и строка «ⓘ Не этим
 * шрифтом: …» в конце.
 *
 * SB122 (uiux-settings-groups §3): аккордеон внутри группы запрещён —
 * список раскрывается текстовой ссылкой «Все места · N ›» (48 px на
 * касании, 32 с 1024; `aria-expanded`). Знак «›» — переход внутри панели.
 *
 * Тексты — только из реестра `FONT_ROLES` (@chaiberi/shared): тот же реестр
 * питает подсказки в панелях элементов, списки не расходятся.
 */

import { FONT_ROLES, fontRoleUsageCount, type FontRole } from "@chaiberi/shared";
import { ChevronRight, Info } from "lucide-react";
import { useId } from "react";

import { SettingsSubgroup } from "../../ui/settings-groups";
import { cn } from "@/lib/utils";

interface Props {
  role: FontRole;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FontUsageList({ role, open, onOpenChange }: Props) {
  const info = FONT_ROLES[role];
  const listId = useId();
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => onOpenChange(!open)}
        className="type-caption inline-flex min-h-12 items-center gap-1 self-start rounded-md font-medium text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring lg:min-h-8"
      >
        Все места · {fontRoleUsageCount(role)}
        <ChevronRight aria-hidden className={cn("size-4 shrink-0 transition-transform", open && "rotate-90")} />
      </button>
      {open ? (
        <div id={listId} className="flex flex-col gap-3" data-testid={`font-usage-${role}`}>
          {info.usage.map((group) => (
            <SettingsSubgroup key={group.group} title={group.title} className="gap-1 pt-0">
              <ul className="flex list-disc flex-col gap-1 pl-5">
                {group.items.map((item) => (
                  <li key={item} className="type-body break-words text-on-surface">
                    {item}
                  </li>
                ))}
              </ul>
            </SettingsSubgroup>
          ))}
          {info.notThis ? (
            <p className="type-caption flex items-start gap-2 text-on-surface-variant">
              <Info aria-hidden className="mt-0.5 size-4 shrink-0" />
              <span className="min-w-0 break-words">{info.notThis}</span>
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
